import { ApiParameter, post } from "@insite/client-framework/Services/ApiService";
import { AddressValidationResultModel, ValidatedAddressModel } from "@insite/client-framework/Types/ApiModels";

export interface AddressValidationApiParameter extends ApiParameter {
    companyName?: string;
    address1?: string;
    address2?: string;
    city?: string;
    stateProvince?: string;
    zipPostalCode?: string;
    country?: string;
}

const addressValidationUrl = "/api/piyovi/validateaddress";

const toValidatedAddress = (raw: any): ValidatedAddressModel | undefined => {
    // Accept both the normalized backend DTO (single object) and the raw Piyovi payload
    // (validatedAddress is an ARRAY of candidates).
    const candidate = Array.isArray(raw) ? raw[0] : raw;
    if (!candidate) {
        return undefined;
    }
    const address: ValidatedAddressModel = {
        address1: candidate.address1,
        address2: candidate.address2,
        city: candidate.city,
        stateProvince: candidate.stateProvince,
        zipPostalCode: candidate.zipPostalCode,
        country: candidate.country,
    };
    // Only treat it as a real suggestion when it carries usable address data.
    return address.address1 || address.city || address.zipPostalCode ? address : undefined;
};

const toErrorMessages = (payload: any): string[] => {
    // Normalized DTO exposes `messages: string[]`; raw Piyovi exposes `errors: [{ code, message }]`.
    if (Array.isArray(payload?.messages)) {
        return payload.messages.filter(Boolean);
    }
    if (Array.isArray(payload?.errors)) {
        return payload.errors
            .map((e: any) => (typeof e === "string" ? e : e?.message))
            .filter(Boolean);
    }
    return [];
};

export async function getAddressValidationResult(
    parameter: AddressValidationApiParameter
): Promise<AddressValidationResultModel> {
    try {
        const result: any = await post<AddressValidationApiParameter, any>(addressValidationUrl, parameter);

        // Be defensive: the body may be a stringified JSON, and may be wrapped as { payload }.
        const dto = typeof result === "string" ? JSON.parse(result) : result;
        const payload = dto?.payload ?? dto;

        const technicalError = payload?.technicalError === true;
        const isSuccess = payload?.success === true;
        const isSameAddress = payload?.isSameAddress === true;
        const validatedAddress = toValidatedAddress(payload?.validatedAddress);
        const hasSuggestion = isSuccess && !isSameAddress && !!validatedAddress;
        const errors = toErrorMessages(payload);

        return {
            isSuccess,
            isSameAddress,
            technicalError,
            hasSuggestion,
            validatedAddress,
            errors,
            message: errors[0] ?? (isSuccess ? "Address validated successfully" : "Address could not be verified"),
        };
    } catch {
        // Network/parse failure — treat as a technical error so checkout is not silently passed.
        return {
            isSuccess: false,
            isSameAddress: false,
            technicalError: true,
            hasSuggestion: false,
            validatedAddress: undefined,
            errors: ["Address validation unavailable"],
            message: "Address validation unavailable",
        };
    }
}
